import React, { useState, useEffect } from 'react'
import type { ClienteNota } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'

interface NotaFormProps {
  clienteId: string
  nota?: ClienteNota | null
  onSave: (nota: ClienteNota) => void
  onCancel: () => void
}

const NotaForm: React.FC<NotaFormProps> = ({ clienteId, nota, onSave, onCancel }) => {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    titulo: '',
    conteudo: '',
    tipo_nota: 'geral' as 'geral' | 'importante' | 'lembrete' | 'comercial' | 'financeiro'
  })
  const [loading, setLoading] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    if (nota) {
      setFormData({
        titulo: nota.titulo,
        conteudo: nota.conteudo,
        tipo_nota: nota.tipo_nota
      })
    } else {
      setFormData({
        titulo: '',
        conteudo: '',
        tipo_nota: 'geral'
      })
      setSelectedFiles([])
    }
  }, [nota])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.conteudo.trim()) {
      alert('O conteúdo da nota é obrigatório')
      return
    }

    setLoading(true)

    try {
      const isEditing = !!nota
      const url = isEditing 
        ? `/api/gestao/cliente-notas?id=${nota.id}`
        : '/api/gestao/cliente-notas'
      
      const method = isEditing ? 'PUT' : 'POST'
      
      const payload = isEditing ? {
        titulo: formData.titulo || 'Nota sem título',
        conteudo: formData.conteudo,
        tipo_nota: formData.tipo_nota
      } : {
        cliente_id: clienteId,
        titulo: formData.titulo || 'Nota sem título',
        conteudo: formData.conteudo,
        tipo_nota: formData.tipo_nota,
        usuario_id: user?.id,
        usuario_nome: user?.email || 'Sistema'
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`)
      }

      const result = await response.json()
      console.log(`✅ Nota ${isEditing ? 'atualizada' : 'criada'}:`, result.data)

      // Se há arquivos para upload e não é edição, fazer upload
      if (selectedFiles.length > 0 && !isEditing) {
        console.log('📎 Iniciando upload de arquivos...')
        await uploadFiles(result.data.id)
        console.log('📎 Upload de arquivos concluído')
      }

      console.log('🔄 Chamando onSave callback...')
      onSave(result.data)
      console.log('✅ Callback onSave executado')
      
    } catch (error) {
      console.error('❌ Erro ao salvar nota:', error)
      alert('Erro ao salvar nota. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleFileSelect = (files: File[]) => {
    const validFiles = files.filter(file => {
      // Validar tipo
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
        'application/pdf', 'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain', 'text/csv'
      ]
      
      if (!allowedTypes.includes(file.type)) {
        alert(`Tipo de arquivo não permitido: ${file.name}`)
        return false
      }
      
      // Validar tamanho (10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`Arquivo muito grande: ${file.name}. Máximo 10MB.`)
        return false
      }
      
      return true
    })
    
    setSelectedFiles(prev => [...prev, ...validFiles])
  }

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const uploadFiles = async (notaId: string) => {
    if (selectedFiles.length === 0) return

    console.log(`📎 Preparando upload de ${selectedFiles.length} arquivo(s) para nota ${notaId}`)
    
    const formData = new FormData()
    formData.append('nota_id', notaId)
    
    selectedFiles.forEach(file => {
      formData.append('files', file)
      console.log(`📎 Adicionado arquivo: ${file.name} (${file.size} bytes)`)
    })

    try {
      console.log('📎 Enviando arquivos para API...')
      const response = await fetch('/api/gestao/cliente-nota-anexos', {
        method: 'POST',
        body: formData
      })

      if (!response.ok) {
        throw new Error(`Erro no upload: ${response.status}`)
      }

      const result = await response.json()
      console.log(`✅ ${result.data.length} arquivo(s) enviado(s) com sucesso`)
      
    } catch (error) {
      console.error('❌ Erro no upload:', error)
      alert('Erro ao enviar arquivos. A nota foi salva, mas os anexos falharam.')
      throw error // Re-throw para não mascarar o erro
    }
  }

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) {
      return '🖼️'
    } else if (type === 'application/pdf') {
      return '📄'
    } else if (type.includes('word') || type.includes('document')) {
      return '📝'
    } else {
      return '📎'
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {nota ? 'Editar Nota' : 'Nova Nota'}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Tipo da Nota */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo da Nota
              </label>
              <select
                value={formData.tipo_nota}
                onChange={(e) => handleChange('tipo_nota', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="geral">Geral</option>
                <option value="importante">Importante</option>
                <option value="lembrete">Lembrete</option>
                <option value="comercial">Comercial</option>
                <option value="financeiro">Financeiro</option>
              </select>
            </div>

            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título (opcional)
              </label>
              <input
                type="text"
                value={formData.titulo}
                onChange={(e) => handleChange('titulo', e.target.value)}
                placeholder="Digite um título para a nota..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* Conteúdo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conteúdo *
              </label>
              <textarea
                value={formData.conteudo}
                onChange={(e) => handleChange('conteudo', e.target.value)}
                placeholder="Digite o conteúdo da nota..."
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-vertical"
                required
              />
            </div>

            {/* Anexos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anexos
              </label>
              
              {/* Área de Upload */}
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  isDragging 
                    ? 'border-orange-500 bg-orange-50' 
                    : 'border-gray-300 hover:border-orange-400'
                }`}
                onDragOver={(e) => {
                  e.preventDefault()
                  setIsDragging(true)
                }}
                onDragLeave={(e) => {
                  e.preventDefault()
                  setIsDragging(false)
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  setIsDragging(false)
                  const files = Array.from(e.dataTransfer.files)
                  handleFileSelect(files)
                }}
              >
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-gray-600 mb-2">
                  Arraste arquivos aqui ou{' '}
                  <label className="text-orange-500 hover:text-orange-600 cursor-pointer font-medium">
                    clique para selecionar
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.txt,.csv"
                      onChange={(e) => handleFileSelect(Array.from(e.target.files || []))}
                      className="hidden"
                    />
                  </label>
                </p>
                <p className="text-sm text-gray-500">
                  Imagens, PDFs, documentos (máx. 10MB por arquivo)
                </p>
              </div>

              {/* Lista de Arquivos Selecionados */}
              {selectedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Arquivos selecionados:</h4>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                          {getFileIcon(file.type)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Informações adicionais */}
            <div className="bg-gray-50 p-3 rounded-md">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  {nota ? 'Editando nota existente' : 'Criando nova nota'} • 
                  Data/hora será registrada automaticamente • 
                  Autor: {user?.email || 'Sistema'}
                </span>
              </div>
            </div>

            {/* Botões */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || !formData.conteudo.trim()}
                className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-md hover:from-orange-600 hover:to-red-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                {nota ? 'Atualizar Nota' : 'Criar Nota'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default NotaForm
